import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useRef, useImperativeHandle, useState, useEffect } from 'react';
import { ReactComponent as SvgBlueEyeDisabled } from '../../assets/icons/BlueEyeDisabled.svg.js';
import { InputWrapper, InputPrefixWrapper, InputLabel, StyledInput, InputSuffixWrapper } from './Input.styles.js';
import Typography from '../Typography/Typography.js';
import { Flex } from '../../layout/Flex.js';
import { isValidValue } from '../../methods/common.js';
import 'styled-components';
import COLORS from '../../constants/colors.js';
import '../../constants/fonts.js';
import '../../constants/theme.js';
import '../../constants/files.js';
import '../../constants/color-variant.js';
import '../../constants/zIndexList.js';
import InputHelperLabel from '../common/InputHelperLabel/InputHelperLabel.js';

const getAdditionalPropsBasedOnType = (props) => {
    var _a, _b;
    let LeftNode;
    let RightNode;
    let countMsg;
    let maxLength;
    let maxValue;
    let minValue;
    let step;
    switch (props.type) {
        case "text":
            {
                const { prefixNode, suffixNode, showCharacterCount, value, length = 100, } = props;
                LeftNode = prefixNode;
                RightNode = suffixNode;
                maxLength = length;
                countMsg = showCharacterCount
                    ? `${(_a = value === null || value === void 0 ? void 0 : value.length) !== null && _a !== void 0 ? _a : 0} / ${length}`
                    : "";
            }
            break;
        case "number":
            {
                const { prefixNode, suffixNode, max, min } = props;
                LeftNode = prefixNode;
                RightNode = suffixNode;
                maxValue = max;
                minValue = min;
                step = props === null || props === void 0 ? void 0 : props.step;
                countMsg = `${isValidValue(min) ? `min: ${min}` : ""} ${isValidValue(max) ? `max: ${max}` : ""}`.trim();
            }
            break;
        case "password":
            {
                const { showCharacterCount, value, length = 100 } = props;
                RightNode = jsx(SvgBlueEyeDisabled, {});
                maxLength = length;
                countMsg = showCharacterCount
                    ? `${(_b = value === null || value === void 0 ? void 0 : value.length) !== null && _b !== void 0 ? _b : 0} / ${length}`
                    : "";
            }
            break;
    }
    return {
        LeftNode,
        RightNode,
        countMsg,
        maxLength,
        minValue,
        maxValue,
        step,
    };
};
const Input = forwardRef((props, ref) => {
    const { type, name, label, headingLabel, headingLabelStyleProps, value = "", onChange, onBlur, outline = "border", disabled = false, required = false, helpMsg, errorMsg, onFocus, onPaste, autoFocus = false, id, placeholder, state, inputMode, enableDebounce, debounceInterval = 500, debounceValidationFunction, borderSize, weight, readOnly, } = props;
    const inputRef = useRef(null);
    useImperativeHandle(ref, () => inputRef.current);
    const { LeftNode, RightNode, countMsg, maxLength, maxValue, minValue, step, } = getAdditionalPropsBasedOnType(props);
    const handleTogglePassword = () => {
        if (type === "password") {
            const isPassword = inputRef.current.type === "password";
            inputRef.current.type = isPassword ? "text" : "password";
        }
    };
    const onClickLeftNode = () => { };
    const onClickRightNode = type === "password" ? handleTogglePassword : () => { };
    /**
     * Debounce functionality start
     */
    const [inputValue, setInputValue] = useState("");
    const [timerId, setTimerId] = useState();
    const debounce = (value, delay) => {
        if (timerId)
            clearTimeout(timerId);
        const TimeoutId = setTimeout(() => {
            onChange(value);
        }, delay);
        setTimerId(TimeoutId);
    };
    useEffect(() => {
        if (inputValue !== "" && enableDebounce)
            debounce(inputValue, debounceInterval);
    }, [inputValue, enableDebounce]);
    /**
     * The below useEffect is added to clear the input field with this scenario
     * When debounce is enabled this component will use inner state for input field and then passes it to outside
     * so even if the value is change from outside state it'll not reflect in UI since the inner state is not cleared or modified
     * so the below condition will work if the outside value is cleared from outside
     */
    useEffect(() => {
        if (enableDebounce && inputValue !== "" && !Boolean(value)) {
            setInputValue("");
        }
    }, [value]);
    const _onChange = (e) => {
        if (enableDebounce && e.target.value === "") {
            setInputValue("");
            onChange(e.target.value);
        }
        else if (enableDebounce) {
            setInputValue(debounceValidationFunction
                ? debounceValidationFunction === null || debounceValidationFunction === void 0 ? void 0 : debounceValidationFunction(e.target.value)
                : e.target.value);
        }
        else
            onChange(e);
    };
    /**
     * Debounce functionality end
     */
    return (jsxs(Flex, Object.assign({ flexDirection: "column", gap: 10 }, { children: [jsx(Typography, Object.assign({ "data-input-heading-label": true, color: COLORS.GREY_900, required: required }, headingLabelStyleProps, { children: headingLabel })), jsxs(InputWrapper, Object.assign({ outline: outline, filled: isValidValue(value), disabled: disabled, state: state, "data-input-wrapper": true, borderSize: borderSize }, { children: [LeftNode ? (jsx(InputPrefixWrapper, { children: LeftNode, onClick: onClickLeftNode, "data-input-affix": true, "data-input-prefix": true })) : null, jsxs(Flex, Object.assign({ flex: 1, flexDirection: "column" }, { children: [label ? (jsx(InputLabel, { children: jsx(Typography, Object.assign({ required: required }, { children: label })) })) : null, jsx(StyledInput
                            // type={type}
                            , Object.assign({ 
                                // type={type}
                                placeholder: placeholder, name: name, id: id, label: label, ref: inputRef, disabled: disabled, required: required, value: enableDebounce
                                    ? String(inputValue)
                                    : String(value), maxLength: maxLength, autoFocus: autoFocus, onChange: _onChange, onFocus: onFocus, onBlur: onBlur, max: maxValue, min: minValue, onPaste: onPaste, autoComplete: "off", inputMode: inputMode, weight: weight, readOnly: readOnly, step: step, onWheel: (e) => e.target instanceof HTMLElement &&
                                    e.target.blur() }, props === null || props === void 0 ? void 0 : props.__experimental__props))] })), RightNode ? (jsx(InputSuffixWrapper, { children: RightNode, onClick: onClickRightNode, "data-input-affix": true, "data-input-suffix": true })) : null] })), helpMsg || errorMsg || countMsg ? (jsxs(Flex, Object.assign({ justifyContent: "space-between" }, { children: [jsxs(Flex, Object.assign({ flexDirection: "column", gap: 10 }, { children: [helpMsg ? (jsx(InputHelperLabel, Object.assign({ type: "desc" }, { children: helpMsg }))) : (jsx(Fragment, {})), errorMsg ? (jsx(InputHelperLabel, Object.assign({ type: "error", id: id + "_error" }, { children: errorMsg }))) : (jsx(Fragment, {}))] })), jsx(InputHelperLabel, Object.assign({ type: "desc" }, { children: countMsg }))] }))) : null] })));
});

export { Input as default };
//# sourceMappingURL=Input.js.map
